<script setup lang="ts">
import { inject, ref, toRefs, watch } from 'vue'

import AssortmentList from '@/components/AssortmentList.vue'
import InputSearch from '@/components/InputSearch.vue'
import ListStores from '@/components/ListStores.vue'
import MapChooser from '@/components/MapChooser.vue'
import StoreInfo from '@/components/StoreInfo.vue'
import useBrandImageResolver from '@/components/useBrandImageResolver'

import calculateDistance from '@/lib/calculateDistance'
import useCenter from '@/lib/useMapCenter'
import type {
  Coordinates,
  Event,
  ExtendedPlace,
  ExtendedPlaces,
  Place,
  Places,
  Store,
} from '@/types'

const props = defineProps<{
  mapRef: google.maps.Map | undefined
  places: { features: Places }
  selectedPlace: (Store & Event) | null
  defaultZoom: number
  showStoreList: boolean
  availableFilters?: string[]
}>()

const { places } = toRefs(props)

const BRAND = inject('BRAND')
const refactStores = ref<ExtendedPlaces>([])

const { center } = useCenter()

const orderByDistance = (places: ExtendedPlaces) =>
  places.sort((a, b) => a.geometry?.distance - b.geometry?.distance)

const calculateStoreDistance = (center: Coordinates, places: Places): void => {
  const extendedPlaces = places.map((place: Store | Event) => ({
    ...place,
    geometry: {
      ...place.geometry,
      distance: calculateDistance(center, place.geometry.coordinates),
    },
  }))

  refactStores.value = orderByDistance(extendedPlaces as ExtendedPlaces)
}

const emit = defineEmits<{
  (e: 'navigateTo', place: Place): void
  (e: 'removeStore'): void
  (e: 'shouldShow', show: boolean): void
  (e: 'updateFilters', filters: string[]): void
}>()

const removeStore = () => emit('removeStore')
const shouldShow = (show: boolean) => emit('shouldShow', show)
const setStore = (place: ExtendedPlace) => emit('navigateTo', place)

watch(
  [center, places],
  () => {
    calculateStoreDistance(center.value as Coordinates, places.value.features)
  },
  { immediate: true },
)

const brandLogo = useBrandImageResolver(BRAND, 'logo.webp')
</script>
<template>
  <div
    id="leftMenu"
    class="absolute left-0 top-0 z-50 max-h-[calc(100vh-28px)] w-full bg-white sm:left-3.5 sm:top-3.5 sm:w-80 sm:overflow-y-scroll sm:rounded-2xl sm:shadow-md"
  >
    <div
      class="w-full items-center justify-center rounded-tl-lg bg-primary-light px-3 py-2.5 text-white"
    >
      <div class="flex items-center justify-center p-2">
        <img :src="brandLogo" alt="logo" class="w-32" width="128" height="68" />
      </div>
      <AssortmentList
        :available-filters="availableFilters"
        @change-filter="(filters) => emit('updateFilters', filters)"
      />
    </div>

    <InputSearch
      :map-ref="props.mapRef"
      :center="center"
      :selected-store="props.selectedPlace ? true : false"
      :default-zoom="props.defaultZoom"
      @remove-store="removeStore"
    />

    <MapChooser
      :show-store-list="props.showStoreList"
      @should-show="shouldShow"
    />

    <div
      class="sm:auto absolute left-0 right-0 h-[80vh] bg-white sm:relative sm:h-auto"
      :class="{ 'hidden sm:block': !props.showStoreList }"
    >
      <KeepAlive>
        <ListStores
          v-if="!props.selectedPlace"
          :map-ref="props.mapRef"
          :places="refactStores"
          @set-store="setStore"
        />
      </KeepAlive>
      <StoreInfo v-if="props.selectedPlace" :place="props.selectedPlace" />
    </div>
  </div>
</template>
